import { useEffect, useMemo, useState } from "react";
import { Card, Col, Form, InputNumber, Row, Select, message } from "antd";
import { debounce } from "lodash";
import CustomDatePicker from "../../../common/CustomDatePicker";
import moment from "moment";
import { getData, postData, putData } from "../../../apiServices/common";
import {
  HOURLY_TARGET_SETUP_GARMENTS_TRACKING_ADD,
  HOURLY_TARGET_SETUP_GARMENTS_TRACKING_EDIT,
  GET_SEWING_LINES,
  GET_QUALITY_INSPECTOR_LIST,
} from "../../../apiServices/API_ENDPOINTS";

const { Option } = Select;

const HourlyTargetForm = ({ form, isEdit, editData, loading: initialLoading, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [lineList, setLineList] = useState([]);
  const [userList, setUserList] = useState([]);

  const getAllLines = async (filterValues) => {
    const query = `${GET_SEWING_LINES}`;
    const payload = {
      per_page: 1000,
      ...filterValues,
    };

    const response = await getData(query, false, payload);
    console.log(response);
    if (response?.status === 200) {
      setLineList(response?.data?.data || []);
    } else {
      message.error("Error getting line data");
    }
  };

  const getAllUsers = async (filterValues) => {
    const query = `${GET_QUALITY_INSPECTOR_LIST}`;
    const payload = {
      ...filterValues,
    };

    const response = await getData(query, false, payload);
    if (response?.status === 200) {
      // Handle both array response and wrapped response
      const userData = Array.isArray(response?.data) ? response.data : response?.data?.data || [];
      setUserList(userData);
    } else {
      message.error("Error getting user data");
    }
  };

  // Debounce search for line and user
  const debounceSearchLines = useMemo(
    () =>
      debounce((value) => {
        getAllLines({ name: value });
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const debounceSearchUsers = useMemo(
    () =>
      debounce((value) => {
        getAllUsers({ name: value });
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    getAllLines();
    getAllUsers();
  }, []);

  useEffect(() => {
    if (isEdit && editData) {
      // Ensure line and user are in the lists
      if (editData?.line_info && !lineList.find((l) => l.id === editData.line_info.id)) {
        setLineList([...lineList, editData.line_info]);
      }

      if (editData?.user_info && !userList.find((u) => u.id === editData.user_info.id)) {
        setUserList([...userList, editData.user_info]);
      }

      form.setFieldsValue({
        id: editData?.id,
        entry_date: editData?.entry_date ? moment(editData.entry_date) : null,
        line_id: editData?.line_id,
        // user_id: editData?.user_id,
        // user_id_card_no: editData?.user_id_card_no,
        hourly_target_qty: editData?.hourly_target_qty,
      });
    } else if (!isEdit) {
      // Set default date to today for create mode
      form.setFieldsValue({
        entry_date: moment(),
      });
    }
  }, [isEdit, editData, form, lineList, userList]);

  const handleUserChange = (userId) => {
    const selectedUser = userList.find((u) => u.id === userId);
    if (selectedUser && selectedUser.additional_info) {
      form.setFieldsValue({
        // user_id_card_no: selectedUser.additional_info.id_card_no,
      });
    }
  };

  const onFinish = async (formValues) => {
    setLoading(true);

    const bodyData = {
      entry_date: moment(formValues?.entry_date).format("YYYY-MM-DD"),
      line_id: Number(formValues?.line_id),
      // user_id: Number(formValues?.user_id),
      // user_id_card_no: formValues?.user_id_card_no,
      hourly_target_qty: Number(formValues?.hourly_target_qty),
    };

    try {
      let response;
      if (isEdit) {
        response = await putData(`${HOURLY_TARGET_SETUP_GARMENTS_TRACKING_EDIT}/${formValues?.id}`, bodyData);
      } else {
        response = await postData(HOURLY_TARGET_SETUP_GARMENTS_TRACKING_ADD, bodyData);
      }

      if (response && response?.code === 200) {
        message.success(`Hourly Target ${isEdit ? "Updated" : "Created"} Successfully`);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="containt-body pt-3">
      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish} disabled={initialLoading || loading}>
          <Form.Item name="id" hidden />
          {/* <Form.Item name="user_id_card_no" hidden /> */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="entry_date"
                label="Date"
                rules={[
                  {
                    required: true,
                    message: "Date is Required",
                  },
                ]}
              >
                <CustomDatePicker style={{ width: "100%" }} size="small" placeholder="Select Date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="line_id"
                label="Line"
                rules={[
                  {
                    required: true,
                    message: "Line is Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select Line"
                  showSearch
                  style={{ width: "100%" }}
                  onSearch={(value) => debounceSearchLines(value)}
                  filterOption={false}
                  size="small"
                >
                  {lineList?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            {/* <Col span={12}>
              <Form.Item
                name="user_id"
                label="User"
                rules={[
                  {
                    required: true,
                    message: "User is Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select User"
                  showSearch
                  style={{ width: "100%" }}
                  onChange={handleUserChange}
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase())
                  }
                  size="small"
                  allowClear
                  optionFilterProp="children"
                >
                  {userList?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name} ({item.additional_info?.id_card_no || "N/A"})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col> */}
            <Col span={12}>
              <Form.Item
                name="hourly_target_qty"
                label="Hourly Target"
                rules={[
                  {
                    required: true,
                    message: "Hourly Target is Required",
                  },
                  {
                    type: "number",
                    min: 0,
                    message: "Hourly Target must be a positive number",
                  },
                ]}
              >
                <InputNumber placeholder="Enter Hourly Target" style={{ width: "100%" }} min={0} size="small" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default HourlyTargetForm;
